/* @flow */
import {Observable} from 'rxjs';
import {combineEpics} from 'redux-observable';
import {prop, path} from 'ramda';
import createDebugger from 'debug';
import {formatTagsToBulk, formatBulkTagResponse, bulkTagSuccessMessage} from 'modules/tags/utils';

const debug = createDebugger('app:modules:tags:epics'); // eslint-disable-line no-unused-vars

import {querystring} from 'trustar/utils/index';
import {REFRESH_INDICATOR_LIST} from 'modules/indicators/action-types';
import {TAGS_ENDPOINT, INDICATOR_TAGS_ENDPOINT} from './types';
import {
  FETCH_ATTRIBUTES,
  FETCH_ATTRIBUTES_SUCCESS,
  FETCH_ATTRIBUTES_FAILURE,

  FETCH_ENCLAVE_TAGS,
  FETCH_ENCLAVE_TAGS_SUCCESS,
  FETCH_ENCLAVE_TAGS_FAILURE,

  FETCH_INDICATOR_TAGS,
  FETCH_INDICATOR_TAGS_SUCCESS,
  FETCH_INDICATOR_TAGS_FAILURE,

  ADD_INDICATOR_TAG,
  ADD_INDICATOR_TAG_SUCCESS,
  ADD_INDICATOR_TAG_FAILURE,
  ADD_INDICATOR_TAGS_BULK,

  REMOVE_INDICATOR_TAG,
  REMOVE_INDICATOR_TAG_SUCCESS,
  REMOVE_INDICATOR_TAG_FAILURE,
} from './action-types';
import {INITIALIZE} from 'modules/app/action-types';
import {success, error, warn} from '../alerts/actions';
import {fetchIndicatorMetadata} from 'modules/indicators/actions';
import {normalizeIndicatorShape} from 'modules/indicators/utils';
import {fromJS, List, Map} from 'immutable';
import {MAX_TAG_LENGTH} from '../reports/constants';
import {SET_ENCLAVE_TAG_FAILURE} from '../reports/action-types';

const indicatorsUrl = (params, url) => querystring(params, url);

const fetchAttributesEpic = (action$, store, {ajax}) =>
  action$.ofType(FETCH_ATTRIBUTES, INITIALIZE)
    .mergeMap(() =>
      ajax.get(`${TAGS_ENDPOINT}/public-tags`)
        .map(prop('response'))
        .map(tags => ({
          type: FETCH_ATTRIBUTES_SUCCESS,
          payload: tags,
        }))
        .catch(err => Observable.of({type: FETCH_ATTRIBUTES_FAILURE}))
    );

const fetchEnclaveTagsEpic = (action$, store, {ajax}) =>
  action$.ofType(FETCH_ENCLAVE_TAGS, INITIALIZE)
    .mergeMap(() =>
      ajax.get(`${TAGS_ENDPOINT}/enclave-tags`)
        .map(prop('response'))
        .map(tags => ({
          type: FETCH_ENCLAVE_TAGS_SUCCESS,
          payload: tags,
        }))
        .catch(err => Observable.of({type: FETCH_ENCLAVE_TAGS_FAILURE}))
    );

const fetchIndicatorTagsEpic = (action$, store, {ajax}) =>
  action$.ofType(FETCH_INDICATOR_TAGS, INITIALIZE, REFRESH_INDICATOR_LIST)
    .mergeMap(() => {
      return ajax.get(INDICATOR_TAGS_ENDPOINT)
        .map(prop('response'))
        .map(tags => ({
          type: FETCH_INDICATOR_TAGS_SUCCESS,
          payload: tags,
        }))
        .catch(err => Observable.of({type: FETCH_INDICATOR_TAGS_FAILURE}));
    });

const addIndicatorTagEpic = (action$, store, {ajax}) =>
  action$.ofType(ADD_INDICATOR_TAG)
    .mergeMap(({payload}) => {
      const {enclaveGuid, name, type, value} = payload;
      if (!name) return Observable.of(warn('Tag name must exist.')); // Check name length... don't add an existing tag to the store
      return ajax.postJSON(`${INDICATOR_TAGS_ENDPOINT}?type=${type}&value=${value}`, fromJS({
        enclaveId: enclaveGuid,
        name
      }))
        .map(prop('response'))
        .mergeMap(response => {
          return [
            {type: ADD_INDICATOR_TAG_SUCCESS, payload: response},
            fetchIndicatorMetadata(type, value),
            success(`${response.name} tag added.`),
          ];
        })
        .catch(err => {
          const message = path(['xhr', 'response', 'message'], err);
          return [
            {type: ADD_INDICATOR_TAG_FAILURE},
            error(message ? message : 'Indicator tag could not be added.'),
          ];
        });
    });

const indicatorTagErrorMessageObservable = (message: string) => {
  const err = new Error(message);
  return Observable.of({
    type: ADD_INDICATOR_TAG_FAILURE,
    error: true,
    payload: err,
    meta: {
      alert: { type: 'error', text: err.message },
    },
  });
}

const addIndicatorTagsBulk = (action$, store, {ajax}) =>
  action$
    .ofType(ADD_INDICATOR_TAGS_BULK)
    .map(prop('payload'))
    .mergeMap(({type, value, enclaveIds, addedTags, removedTags}) => {
      let message;

      const tagCombos = enclaveIds.flatMap(enclaveId =>
        addedTags.map(name => {
          if (!name.trim()) message = 'No tag name specified.';
          if (name.length > MAX_TAG_LENGTH) message = `Tag name must be less than ${MAX_TAG_LENGTH} characters.`;
          return {type, value, enclaveGuid: enclaveId, name};
        }));

      const indicatorTagCombinationsForDeletion = removedTags && enclaveIds.flatMap(enclaveId =>
        removedTags.map(name => {
          if (!name.trim()) message = 'No tag name specified.';
          if (name.length > MAX_TAG_LENGTH) message = `Tag name must be less than ${MAX_TAG_LENGTH} characters.`;
          return {type, value, enclaveGuid: enclaveId, name};
        }));

      if (message) {
        return indicatorTagErrorMessageObservable(message);
      }
      const formattedTagsObject = formatTagsToBulk(tagCombos, indicatorTagCombinationsForDeletion);
      const formattedBulkTagPayload = {
        indicator: {
          value,
          type,
        },
        ...formattedTagsObject
      };

      return ajax.postJSON(`${INDICATOR_TAGS_ENDPOINT}`, formattedBulkTagPayload)
        .map(prop('response'))
        .map(response => fromJS(response))
        .mergeMap(tagResponse => {
          const {addedTags = List(), removedTags = List()} = formatBulkTagResponse(tagResponse, tagCombos).toJS();
          const postOpActions = [fetchIndicatorMetadata(type, value), {type: FETCH_INDICATOR_TAGS}];
          const tagOperations = addedTags
            .map(tag => ({type: ADD_INDICATOR_TAG_SUCCESS, payload: {value, type, ...tag}}))
            .concat(removedTags.map(tag => ({
              type: REMOVE_INDICATOR_TAG_SUCCESS,
              payload: normalizeIndicatorShape({value, type, ...tag})
            })))
            .concat(postOpActions);

          const successMessage = bulkTagSuccessMessage(addedTags, removedTags);
          return successMessage ? tagOperations.concat(success(successMessage)) : tagOperations;
        })
        .catch(err => {
          const message = path(['xhr', 'response', 'message'], err)
        });
    });

const deleteIndicatorTagEpic = (action$, store, {ajax}) =>
  action$.ofType(REMOVE_INDICATOR_TAG)
    .mergeMap(({payload}) => {
      return ajax.postJSON(indicatorsUrl(payload, `${INDICATOR_TAGS_ENDPOINT}/delete`))
        .map(prop('response'))
        .mergeMap(({guid, name}) => {
          return [
            {type: FETCH_INDICATOR_TAGS},
            {type: REMOVE_INDICATOR_TAG_SUCCESS, payload: normalizeIndicatorShape(payload)},
            success(`${name} tag removed from indicator.`),
          ];
        })
        .catch(err => {
          return [
            {type: REMOVE_INDICATOR_TAG_FAILURE},
            error('Indicator tag could not be removed.'),
          ];
        });
    });

export default combineEpics(
  fetchAttributesEpic,
  fetchEnclaveTagsEpic,
  fetchIndicatorTagsEpic,
  addIndicatorTagEpic,
  deleteIndicatorTagEpic,
  addIndicatorTagsBulk
);



// WEBPACK FOOTER //
// ./src/app/modules/tags/epics.js